import '../App.css';
import React, {useState, useEffect, useRef } from 'react'
import ChessBoard from 'chessboardjsx';
import { Chess } from 'chess.js';
import opp from '../utils/opp.js';
import {Stack, Box, useMediaQuery, useTheme} from '@mui/material';
import RoomSideBar from '../components/SideBars/RoomSideBar.js';
import GameSideBar from '../components/SideBars/GameSideBar.js';
import MultiplayerOpponentBar from '../components/SideBars/MultiplayerOpponentBar.js';
import MultiplayerPlayerBar from '../components/SideBars/MultiplayerPlayerBar.js';
import GameEndingModal from '../components/GameEndingModal.js';
import ConfirmDialog from '../components/ConfirmDialog.js';
import BlindBoard from '../components/BlindBoard.js';
import PromotionDialog from '../components/PromotionDialog.js';
import SoundControls from '../components/SoundControls.js';
import { onMove } from '../utils/sound.js';

import { useParams, useLocation, useHistory } from "react-router-dom";



function Game({socket}) {

    const [fen, setFen] = useState("start");
    const [gameState, setGameState] = useState('waiting');
    const [gameMode, setGameMode] = useState('Normal');
    const [opGameMode, setOpGameMode] = useState('Normal')
    const [myLastMove, setMyLastMove] = useState('');
    const [opLastMove, setOpLastMove] = useState('');
    const [drawOffer, setDrawOffer] = useState('');
    const [drawConfirmation, setDrawConfirmation] = useState(false);
    const [resignConfirmation, setResignConfirmation] = useState(false);  
    const [exitConfirmation, setExitConfirmation] = useState(false);
    const [gameResult, setGameResult] = useState('');
    const [gameResultDetail, setGameResultDetail] = useState('');
    const [hints, setHints] = useState(0);
    const [opHints, setOpHints] = useState(0);
    const [pendingPromotion, setPendingPromotion] = useState(null);

    const { id } = useParams();
    const loc = useLocation();
    const color = loc.state.color;
    const player = loc.state.player;
    const history = useHistory();
    
    const [myTime, setMyTime] = useState({minutes: loc.state.timeControl, seconds: 0});
    const [opTime, setOpTime] = useState({minutes: loc.state.timeControl, seconds: 0});
    const [turn, setTurn] = useState(false);
    let game = useRef(null);

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const [windowDimensions, setWindowDimensions] = useState(getWindowDimensions());
    // Fix mobile screen bug >> need to test
    function handleResize() {
        setWindowDimensions(getWindowDimensions());
      }
    // handleResize();

    useEffect(() => {
  
      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        if (game.current === null) {
            game.current = new Chess();
            const date = new Date();
            game.current.header('date', `${date.getFullYear()}.${date.getMonth()}.${date.getDate()}`)
        }
        socket.on('updated_game', (data) => {

                const opMove = game.current.move(data.opLastMove);
                onMove(opMove, game.current);
                setFen(game.current.fen());
                let moves = game.current.history();
                setOpLastMove(moves[moves.length - 1]);
                setTurn(true);
                setOpTime(data[`${opp(color)}Time`]);
                setOpHints(data.hint);
                setDrawOffer('')
            });

        socket.on('start_game', (data) => {
            setGameMode(data[color]);
            setTurn(color === 'white');
            setGameState('start');
            setOpGameMode(data[opp(color)]);
            game.current.header(color, data[player], opp(color), data[opp(player)])
            if (data[color] === 'Custom') {
                setHints(data[`${color}Hint`])
                setOpHints(data[`${opp(color)}Hint`])
            }
        })

        socket.on('ended_game', (data) => {
            setGameState('end');
            setGameResult(opp(data.result));
            setGameResultDetail(data.by);
            setDrawOffer('')
        })

        socket.on('draw_offer', (data) => {
            setDrawOffer('your opponent offered a draw, accept?')
        })

        socket.on('opponent_disconnected', () => {
            setGameResult('win');
            setGameResultDetail('abandonment');
            setGameState('end');
            setDrawOffer('');
        })

    }, [socket, color, player])


    const isPromotion = (from, to) => {
        const piece = game.current.get(from);
        if (!piece || piece.type !== 'p') return false;
        const rank = to.charAt(1);
        return (piece.color === 'w' && rank === '8') || (piece.color === 'b' && rank === '1');
    }

    const handleDrop = ({sourceSquare, targetSquare}) => {

        if (game.current.get(sourceSquare)?.color !== color.charAt(0) || gameState !== 'start') {
            return;
        }
        if (isPromotion(sourceSquare, targetSquare)) {
            setPendingPromotion({from: sourceSquare, to: targetSquare});
            return;
        }
        completeMove({from: sourceSquare, to: targetSquare});
    }

    const completeMove = (drop) => {
        const move = game.current.move(drop);
        if (move === null) {return;}
        onMove(move, game.current);

        setFen(game.current.fen());
        setTurn(false);
        if (checkGameEnd(game.current)) {return;}
        socket.emit("update_game", {
            fen: game.current.fen(),
            color: color,
            roomID: id,
            lastMove: drop,
            myTime: myTime,
            myHint: hints - 1})

        if (gameMode === 'Using hint...') {
            setGameMode('Custom');
            setHints(hints - 1);
            let moves = game.current.history();
            setMyLastMove(moves[moves.length - 1]);
        }
        setDrawOffer('')
    }

    const handlePromotionSelect = (piece) => {
        const drop = {...pendingPromotion, promotion: piece};
        setPendingPromotion(null);
        completeMove(drop);
    }

    const handleBlindSubmit = (m) => {
        let move = game.current.move(m, {sloppy: true});
        if (move === null || !turn) {
            return {'error': `${m} is not a valid move`, 'move': m};
        } else {
            onMove(move, game.current);
            setFen(game.current.fen());
            setTurn(false);
            if (checkGameEnd(game.current)) { return; }
            let moves = game.current.history();
            m = moves[moves.length - 1]
            
            socket.emit("update_game", {
                myTime: myTime, 
                fen: game.current.fen(), 
                roomID: id, color: color, 
                lastMove: m,
                myHint: hints})
            setDrawOffer('');
            return {'error': '', 'move': m}
        }
        
    }

    const checkGameEnd = (g) => {

        if (!g.isGameOver()) {
            return false;
        } else {
            socket.emit("update_game", {myTime: myTime, fen: g.fen(), roomID: id, color: color, lastMove:g.history()[g.history().length - 1]});
            if (g.isCheckmate()) {
                // let res = {result: 'win', by: 'checkmate'};
                setGameResult('win');
                setGameResultDetail('checkmate')
                endGame('win', 'checkmate');
            }
            else if (myTime.minutes === 0 && myTime.seconds === 0) {
                setGameResult('lose');
                setGameResultDetail(`timeout`)
                endGame('lose', 'timeout')
            }
            else if (g.isThreefoldRepetition()) {
                setGameResult('draw');
                setGameResultDetail('tfr');
                endGame('draw', 'repetition')
            }
            else if (g.isStalemate()) {
                setGameResult('draw');
                setGameResultDetail('stalemate');
                endGame('draw', 'stalemate')
            }
            else if (g.isDraw()) {
                setGameResult('draw');
                setGameResultDetail('50 moves rule');
                endGame('draw', '50 moves rule')
            }

            // socket.emit("update_game", {myTime: myTime, fen: g.fen(), roomID: id, color: color, lastMove: g.history()[0]});
            setGameState('end');
            return true;
        }
    }

    const handleResign = () => {
        setGameResult('lose');
        setGameState('end');
        setGameResultDetail('resignation');
        setResignConfirmation(false);
        endGame('lose', 'resignation')
        // socket.emit('end_game', {result: 'lose', by: 'resignation', color: color, roomID: id});
    }

    const handleDrawOffer = () => {

        if (drawOffer === 'your opponent offered a draw, accept?') {

            endGame('draw', 'agreement')

        } else if (drawOffer === '') {

            socket.emit('draw_offer', {roomID: id})
            setDrawOffer('you offered a draw')
            // TODO: remove draw offer after move! 

        }
        
    }

    const endGame = (result, by) => {
        socket.emit('end_game', {result: result, by: by, color: color, roomID: id});
    }

    const handleUseHint = () => {
        if (hints > 0) {
            setGameMode('Using hint...');
        } else {
            return {'error': 'You have no hint left'}
        }
    }

    const onExport = () => { 
        let result = '*';
        if (gameResult === 'draw') {
            result = "1/2-1/2"
        } else if ((gameResult === 'win' && color === 'white') || (gameResult === 'lose' && color === 'white')) {
            result = "1-0"
        } else {
            result = "0-1"
        }
       
        game.current.header(
            'Site', 'ChessBlindly',
            'Result', result,
        )

        const data = {'pgn' : game.current.pgn()}
        const options = {
          method: "POST", 
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json'}, 
          body: JSON.stringify(data)
        };
        
        fetch("https://lichess.org/api/import", options)
        .then((response) => response.json())
        .then((data) => window.open(data.url));
      }

    return (
        <>
        <header className="App-header">
        <script async src="https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=ca-pub-1369506265455012"
            crossorigin="anonymous"></script>
        <SoundControls/>
        <GameEndingModal result={gameResult} detail={gameResultDetail} open={gameState === 'end'} setOpen={setGameState}/>
        <ConfirmDialog title={'Resign'} description={'Are you sure that you want to resign?'} open={resignConfirmation && gameState !== 'afterGame'} setOpen={setResignConfirmation}  handleConfirm={handleResign}/>
        <ConfirmDialog title={'Draw offer'} description={'Are you sure that you want to offer a draw?'} open={drawConfirmation && gameState !== 'afterGame'} setOpen={setDrawConfirmation} handleConfirm={handleDrawOffer}/>
        <ConfirmDialog title={'Exit'} description={'The game data will be lost, are you sure to exit?'} open={exitConfirmation && gameState === 'afterGame'} setOpen={setExitConfirmation} handleConfirm={() => history.push({pathname: '/home'})}/>
        <PromotionDialog open={!!pendingPromotion} onSelect={handlePromotionSelect}/>
        {isMobile ?
       <Box sx={{margin: 'auto', display: 'flex', flexDirection: 'column', width: '100%', p: 1 }}>
        <MultiplayerOpponentBar
            opTime={opTime}
            setOpTime={setOpTime}
            turn={turn}
            opGameMode={opGameMode}
            gameState={gameState}
            roomID={loc.state.roomID}
        />
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', my: 2 }}>
            {(gameMode === 'Normal' || gameMode === 'Using hint...')
                ? <Board
                    position={fen}
                    onDrop={handleDrop}
                    color={color}
                    windowDimensions={windowDimensions}
                />
                : <BlindBoard
                    opLastMove={opLastMove}
                    submitMove={handleBlindSubmit}
                    myLastMove={myLastMove}
                    setMyLastMove={setMyLastMove}
                    isTurn={turn}
                    hint={hints}
                    useHint={handleUseHint}
                    mode={gameMode}
                />
            }
        </Box>
        <MultiplayerPlayerBar
            myTime={myTime}
            setMyTime={setMyTime}
            turn={turn}
            gameMode={gameMode}
            gameState={gameState}
            setDrawConfirmation={setDrawConfirmation}
            setResignConfirmation={setResignConfirmation}
            sx={{ mt: 2 }}
            onExport={onExport}
            onExit={() => setExitConfirmation(true)}
        />
    </Box>
        
        : (
            <Stack direction={{ xs: 'column', md: 'row' }} spacing='50px'>
                
                <RoomSideBar roomID={loc.state.roomID} mode={gameMode} opMode={opGameMode} status={gameState} hints={hints} opHints={opHints}/>
                {/* <Board position={fen} onDrop={handleDrop} color={color}/> */}

                {(gameMode === 'Normal' || gameMode === 'Using hint...') ? (
                    <Board position={fen} onDrop={handleDrop} color={color} windowDimensions={windowDimensions}/>
                    ) 
                    : (
                    <BlindBoard opLastMove={opLastMove} submitMove={handleBlindSubmit} myLastMove={myLastMove} setMyLastMove={setMyLastMove} isTurn={turn} hint={hints} useHint={handleUseHint} mode={gameMode}/>
                )}
                
                <GameSideBar myTime={myTime} setMyTime={setMyTime} opTime={opTime} setOpTime={setOpTime} turn={turn} color={color} 
                gameState={gameState} setDrawConfirmation={setDrawConfirmation} setResignConfirmation={setResignConfirmation} onExport={onExport} onExit={() => setExitConfirmation(true)}/>
            </Stack>
            )
        }
        </header>
        </>
    )
}

function Board({position, onDrop, color, windowDimensions}) {
    let size = windowDimensions.width;
    let width = 720
    if (size < 720) {
        width = size - 10;
    }
    return (
        <div className="App">
            <ChessBoard position={position} onDrop={onDrop} orientation={color} width={width} transitionDuration={150}
                lightSquareStyle={{ backgroundColor: '#dbe4ff' }} darkSquareStyle={{ backgroundColor: '#5b6bb5' }}/>
        </div>
    )
}

function getWindowDimensions() {
    const { innerWidth: width, innerHeight: height } = window;
    return {
      width,
      height
    };
  }

export default Game;